import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  TextField,
  Divider,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import HistoryIcon from "@mui/icons-material/History";
import { useInputDefinitionsStore } from "./store/inputDefinitionsStore";

const formatTs = (ms) => {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
};

export default function VersionHistoryDialog({ open, definitionId, definitionName, onClose, onRestored }) {
  const { listVersions, snapshot, restoreVersion } = useInputDefinitionsStore();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [label, setLabel] = useState("");
  const [confirmRestoreId, setConfirmRestoreId] = useState(null);

  const refresh = async () => {
    if (!definitionId) return;
    setLoading(true);
    setErr("");
    try {
      const v = await listVersions(definitionId);
      setVersions(v);
    } catch (e) {
      setErr(e?.message || "Failed to load versions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, definitionId]);

  const handleSnapshot = async () => {
    if (!definitionId) return;
    setErr("");
    setInfo("");
    try {
      const id = await snapshot(definitionId, label || "snapshot");
      if (id) {
        setInfo(`Snapshot saved${label ? `: ${label}` : ""}`);
        setLabel("");
        await refresh();
      }
    } catch (e) {
      setErr(e?.message || "Snapshot failed");
    }
  };

  const handleRestore = async (versionId) => {
    setErr("");
    setInfo("");
    try {
      await restoreVersion(versionId);
      setInfo("Restored. A pre-restore backup was created automatically.");
      await refresh();
      if (onRestored) onRestored();
    } catch (e) {
      setErr(e?.message || "Restore failed");
    } finally {
      setConfirmRestoreId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon fontSize="small" />
          Version history — {definitionName}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr("")}>{err}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setInfo("")}>{info}</Alert>}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label="Snapshot label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<BookmarkAddIcon />}
            onClick={handleSnapshot}
            disabled={!definitionId}
          >
            Snapshot now
          </Button>
        </Box>
        <Divider sx={{ my: 1 }} />

        {loading && <Typography variant="caption">Loading…</Typography>}
        {!loading && versions.length === 0 && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No versions yet. The first auto-snapshot will be written ~60s after your first edit.
          </Typography>
        )}
        <List dense>
          {versions.map((v) => (
            <ListItem key={v.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <span>{formatTs(v.created)}</span>
                    {v.label && (
                      <Chip
                        size="small"
                        label={v.label}
                        color={v.label === "pre-restore" ? "warning" : "primary"}
                      />
                    )}
                  </Box>
                }
                secondary={v.user_id || ""}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Restore this version">
                  <IconButton size="small" onClick={() => setConfirmRestoreId(v.id)}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Dialog open={!!confirmRestoreId} onClose={() => setConfirmRestoreId(null)}>
          <DialogTitle>Restore this version?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              The current state will be auto-saved as a <strong>pre-restore</strong> snapshot
              first, so you can undo this restore.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRestoreId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleRestore(confirmRestoreId)}
            >
              Restore
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
